import { calculateTotalJourneyBaggageCost } from 'checkout/lib/utils/flights/extras'
import { mergeFees } from 'checkout/lib/utils/payments/fees'
import { excludeNullOrUndefined } from 'checkout/utils'
import { sum } from 'lib/array/arrayUtils'
import { createSelector } from 'reselect'
import {
  getFlightDateChangeSearchAirlineFeeJourneys,
  getFlightItemViewLuxPlusRegularPrice,
  getFlightItems,
  getFlightItemsView,
  getIsCurrentFlightDateChangeSelectionValid,
  getIsFlightDateChangeSearchLoading,
  getShouldHideEstimatedAirlineChangeFee,
} from 'checkout/selectors/view/flights'
import { VIRGIN_AUSTRALIA } from 'constants/flight'
import { findPostPurchaseCheckout, isLuxPlusSubscriptionItem } from 'lib/checkout/checkoutUtils'
import { getDateChangeJourneyV2IdKey } from 'lib/flights/flightUtils'
import { getCanRedeemLuxPlusBenefitsOnOrder, getIsEnabledFlightsLuxPlusPricing } from 'luxPlus/selectors/featureToggle'
import { floatify } from 'lib/maths/mathUtils'

function getSelectedDateChangeJourneys(
  flightItems: Array<App.Checkout.FlightItem>,
  journeysById: Record<string, App.AnyJourney>,
): { hasSelectedJourneys: boolean; hasRequiredData: boolean; journeys: Array<App.JourneyV2> } {
  const dateChangeFlightItems = flightItems.filter(item => item.isFlightDateChange)
  const journeyKeys = dateChangeFlightItems.flatMap(item =>
    item.flights.map(flight => getDateChangeJourneyV2IdKey(item.reservationId!, item.searchId!, flight.journeyId)),
  )
  const journeys = journeyKeys.map(key => journeysById[key] as App.JourneyV2 | undefined)

  return {
    hasSelectedJourneys: journeyKeys.length > 0,
    hasRequiredData: journeyKeys.length > 0 && journeys.every(Boolean),
    journeys: journeys.filter(excludeNullOrUndefined),
  }
}

function getSelectedAirlineChangeFee(journeys: Array<App.JourneyV2>): number {
  return sum(journeys, journey => journey.price?.all?.airlineChangeFee ?? 0)
}

export const getFlightTotals = createSelector(
  (state: App.State) => getFlightItemsView(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => getIsCurrentFlightDateChangeSelectionValid(state),
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getCanRedeemLuxPlusBenefitsOnOrder(state),
  (state: App.State) => getIsEnabledFlightsLuxPlusPricing(state),
  (
    flightItemsWithStatus,
    postPurchaseMode,
    isCurrentFlightDateChangeSelectionValid,
    checkoutItems,
    canRedeemLuxPlusBenefitsOnOrder,
    isFlightsLuxPlusPricingEnabled,
  ): App.WithDataStatus<App.Checkout.ItemViewTotals> => {
    if (postPurchaseMode === 'flight-change-dates' && !isCurrentFlightDateChangeSelectionValid) {
      return {
        hasRequiredData: true,
        data: {
          price: 0,
          memberPrice: 0,
          value: 0,
          surcharge: 0,
          taxesAndFees: 0,
          otherFees: {},
          memberValue: 0,
          extraGuestSurcharge: 0,
        },
      }
    }

    const definedItems = flightItemsWithStatus.data.filter(excludeNullOrUndefined)
    const canUseLuxPlusFlightPricing = canRedeemLuxPlusBenefitsOnOrder || checkoutItems.some(isLuxPlusSubscriptionItem)
    const getItemPrice = (itemView: App.Checkout.FlightItemView): number => {
      const selectedPrice = itemView.totals.price ?? itemView.quotedFare ?? 0
      if (!isFlightsLuxPlusPricingEnabled || canUseLuxPlusFlightPricing) {
        return selectedPrice
      }

      return getFlightItemViewLuxPlusRegularPrice(itemView) ?? selectedPrice
    }

    const itemTotals: App.Checkout.ItemViewTotals = {
      price: floatify(sum(definedItems, getItemPrice)),
      memberPrice: 0,
      value: sum(definedItems, itemView => itemView.totals.value || 0),
      surcharge: 0,
      taxesAndFees: 0,
      otherFees: {
        ...mergeFees(definedItems.map(itemView => itemView.totals.otherFees).filter(excludeNullOrUndefined)),
        flightBaggageTotal: sum(definedItems, itemView => calculateTotalJourneyBaggageCost(itemView)),
      },
      memberValue: 0,
      extraGuestSurcharge: 0,
    }

    return {
      hasRequiredData: flightItemsWithStatus.hasRequiredData,
      data: itemTotals,
    }
  },
)

export const getFlightsDateChangeFees = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => state.orders.flightDetails,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => getFlightItems(state),
  (state: App.State) => state.flights.journeysById,
  (state: App.State) => getFlightDateChangeSearchAirlineFeeJourneys(state),
  (state: App.State) => getShouldHideEstimatedAirlineChangeFee(state),
  (state: App.State) => getIsFlightDateChangeSearchLoading(state),
  (state: App.State) => getIsCurrentFlightDateChangeSelectionValid(state),
  (existingOrder, allFlightDetails, postPurchaseMode, flightItems, journeysById, searchAirlineFeeJourneys, hideEstimatedAirlineChangeFee, isFlightDateChangeSearchLoading, isCurrentFlightDateChangeSelectionValid): App.WithDataStatus<App.Checkout.ItemViewTotals> => {
    const isFlightDateChange = postPurchaseMode === 'flight-change-dates' && !!existingOrder

    if (!isFlightDateChange) {
      return {
        hasRequiredData: true,
        data: {
          price: 0,
          memberPrice: 0,
          value: 0,
          surcharge: 0,
          taxesAndFees: 0,
          memberValue: 0,
          extraGuestSurcharge: 0,
        },
      }
    }

    const selectedDateChangeJourneys = isCurrentFlightDateChangeSelectionValid ?
        getSelectedDateChangeJourneys(flightItems, journeysById) :
        { hasSelectedJourneys: false, hasRequiredData: false, journeys: [] }
    const airlineFeeJourneys = searchAirlineFeeJourneys.length > 0 ? searchAirlineFeeJourneys : selectedDateChangeJourneys.journeys
    const isSelectedDateChangeJourneyLoading = selectedDateChangeJourneys.hasSelectedJourneys && !selectedDateChangeJourneys.hasRequiredData
    const isAirlineChangeFeeLoading = isFlightDateChangeSearchLoading || isSelectedDateChangeJourneyLoading

    const flightItemId = existingOrder?.flightItems[0]?.id
    const flightDetails = allFlightDetails[flightItemId ?? '']
    const airlineChangeFee = hideEstimatedAirlineChangeFee || isAirlineChangeFeeLoading ? 0 : airlineFeeJourneys.length > 0 ?
        getSelectedAirlineChangeFee(airlineFeeJourneys) :
      flightDetails?.flight?.flightChangePenalties?.fees.totalAmount || 0
    const luxuryEscapesChangeFee = flightDetails?.flight?.flightChangePenalties?.fees.handlingFee || 0
    const totalChangeFees = airlineChangeFee + luxuryEscapesChangeFee

    return {
      hasRequiredData: !!flightDetails && !isAirlineChangeFeeLoading && (!selectedDateChangeJourneys.hasSelectedJourneys || selectedDateChangeJourneys.hasRequiredData),
      data: {
        price: totalChangeFees,
        memberPrice: 0,
        value: totalChangeFees,
        surcharge: 0,
        taxesAndFees: 0,
        memberValue: 0,
        extraGuestSurcharge: 0,
      },
    }
  },
)

/**
 * Total cost of all flights provided by Virgin Australia
 */
export const getVirginFlightTotals = createSelector(
  (state: App.State) => getFlightItemsView(state),
  (flightItemsViewWithStatus): App.WithDataStatus<App.Checkout.ItemViewTotals> => {
    const definedItems = flightItemsViewWithStatus.data
      .filter(excludeNullOrUndefined)
      .filter(view => view.validatingCarrierName === VIRGIN_AUSTRALIA)

    return {
      hasRequiredData: flightItemsViewWithStatus.hasRequiredData,
      data: {
        price: sum(definedItems, itemView => itemView.totals.price || itemView.quotedFare || 0),
        memberPrice: 0,
        value: sum(definedItems, itemView => itemView.totals.value),
        surcharge: 0,
        taxesAndFees: 0,
        otherFees: {
          ...mergeFees(definedItems.map(itemView => itemView.totals.otherFees).filter(excludeNullOrUndefined)),
          flightBaggageTotal: sum(definedItems, itemView => calculateTotalJourneyBaggageCost(itemView)),
        },
        memberValue: 0,
        extraGuestSurcharge: 0,
      },
    }
  },
)

export { default as getIsLuxPlusPricedFlight } from './getLuxPlusPricedFlight'

export const getCartFlightJourneys = createSelector(
  (state: App.State) => getFlightItems(state),
  (state: App.State) => state.flights.journeysById,
  (flightItems, journeys): Array<App.Journey> => {
    // @ts-ignore
    return flightItems.map(item => journeys[item.searchId] as App.Journey).filter(Boolean)
  },
)
